import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Mail, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { GoogleLoginButton } from "@/components/GoogleLoginButton";
import { usePublicSettings } from "@/hooks/usePublicSettings";
import Logo from "@/components/Logo";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const { google_auth_enabled } = usePublicSettings();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const redirectTo = new URLSearchParams(location.search).get("redirect") || "/dashboard";

  // Redirect if already logged in
  useEffect(() => {
    if (user) navigate(redirectTo, { replace: true });
  }, [user, navigate, redirectTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please enter email and password"); return; }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate(redirectTo);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid email or password");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-white" data-testid="login-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-[400px]">
        <Card className="bg-white border-slate-200 shadow-lg" style={{ borderRadius: 16 }}>
          <CardContent className="p-8">
            {/* Logo centered */}
            <div className="flex justify-center mb-6">
              <Logo size="large" theme="light" />
            </div>

            <h2 className="text-xl font-bold text-[#1e293b] text-center mb-1">Welcome back</h2>
            <p className="text-sm text-[#64748b] text-center mb-6">Sign in to your audit portal</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#1e293b] text-sm font-medium">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                  <Input
                    type="email" placeholder="you@company.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11 bg-white border-slate-200 text-[#1e293b] placeholder:text-slate-400 focus-visible:ring-[#6366f1]"
                    required data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1e293b] text-sm font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                  <Input
                    type="password" placeholder="Enter password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11 bg-white border-slate-200 text-[#1e293b] placeholder:text-slate-400 focus-visible:ring-[#6366f1]"
                    required data-testid="login-password-input"
                  />
                </div>
                <div className="text-right">
                  <Link to="/forgot-password" className="text-xs text-[#6366f1] hover:underline font-medium">Forgot password?</Link>
                </div>
              </div>
              <Button
                type="submit" disabled={loading}
                className="w-full h-11 font-bold text-white"
                style={{ background: "#6366f1" }}
                data-testid="login-submit-btn"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in...</> : "Sign In \u2192"}
              </Button>
            </form>

            {google_auth_enabled && (
              <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                <p className="text-sm text-[#64748b] mb-4">Or sign in with</p>
                <GoogleLoginButton redirectPath={redirectTo} />
              </div>
            )}

            <div className="mt-6 pt-5 border-t border-slate-100 text-center">
              <p className="text-sm text-[#64748b]">
                Don't have an account?
              </p>
              <Link to="/audit" className="text-sm text-[#6366f1] hover:underline font-medium">
                Run a free audit first &rarr;
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
